import { AxiosPromise } from "axios";
import { ValidationResult } from "formvalidation/hooks";
import React, { createContext, useEffect, useState } from "react";
import { InPageEditableSectionField } from "./types";
import equal from "fast-deep-equal";

export interface InPageEditingState {
  fieldKey: string | undefined;
  value: any | undefined;
  initialValue: any | undefined;
  isUpdating: boolean;
  error: string | undefined;
  submitFunc: (value: any) => AxiosPromise<server.dto.ApiResponseWrapper<any> | server.dto.ApiResponse>;
}

const defaultInPageEditingState: InPageEditingState = {
  fieldKey: undefined,
  value: undefined,
  initialValue: undefined,
  isUpdating: false,
  error: undefined,
  submitFunc: () => undefined,
}

export interface InPageEditingContextValue {
  enabled: boolean;
  editingField: InPageEditingState;
  startInPageEdit: (field: InPageEditableSectionField) => void;
  updateValue: (value: any) => void;
  saveInPageEdit: (value: any) => void;
  clearInPageEdit: () => void;
  removeItem: (field: InPageEditableSectionField) => void;
}

const defaultInPageEditingContext = {
  enabled: false,
  editingField: defaultInPageEditingState,
  startInPageEdit: () => { },
  updateValue: () => { },
  saveInPageEdit: () => { },
  clearInPageEdit: () => { },
  removeItem: () => { },
}

export const InPageEditingContext = createContext<InPageEditingContextValue>(defaultInPageEditingContext);

const useInPageEditingState = () => {
  const [editingField, setEditingField] = useState<InPageEditingState>(defaultInPageEditingState);
  const [nextField, setNextField] = useState<InPageEditingState | undefined>(undefined);

  useEffect(() => {
    if (editingField.fieldKey === undefined && nextField !== undefined) {
      setEditingField(nextField);
      setNextField(undefined);
    }
  }, [editingField, nextField]);

  return [editingField, setEditingField, setNextField] as const;
}

interface InPageEditingProviderProps {
  enabled: boolean;
  validate: () => Promise<ValidationResult>;
  onValueChanged: (key: string, value: any) => (Promise<void> | void);
  onSuccessfulUpdate?: (key: string, newValue: any) => Promise<void>;
}

export const InPageEditingProvider = (props: React.PropsWithChildren<InPageEditingProviderProps>) => {
  const [editingField, setEditingField, setNextField] = useInPageEditingState();

  const startInPageEdit = (field: InPageEditableSectionField) => {
    if (editingField.fieldKey !== undefined && editingField.fieldKey !== field.key && !editingField.isUpdating) {
      clearInPageEdit();
    }
    setNextField(state => ({
      ...state,
      fieldKey: field.key,
      value: field.inPageEdit!.initialValue,
      initialValue: field.inPageEdit!.initialValue,
      submitFunc: field.inPageEdit!.submitChange,
    }));
  }
  const updateValue = (value: any) => setEditingField(state => ({ ...state, value }));
  const clearInPageEdit = () => setEditingField(defaultInPageEditingState);

  const executeSubmit = async (fieldKey: string, value: any, initialValue: any, submitFunc: (v: any) => AxiosPromise<server.dto.ApiResponseWrapper<any> | server.dto.ApiResponse>): Promise<boolean> => {
    await props.onValueChanged(fieldKey, value);
    try {
      const response = await submitFunc(value);
      if (response.data.hasErrors) {
        setEditingField(state => ({
          ...state,
          value: initialValue,
          isUpdating: false,
          error: response.data.errors[0].message
        }));
        await props.onValueChanged(fieldKey, initialValue);
        return false;
      }
      if (props.onSuccessfulUpdate) {
        await props.onSuccessfulUpdate(fieldKey, value);
      }
    } catch (e: any) {
      console.error(e);
      setEditingField(state => ({
        ...state,
        value: initialValue,
        isUpdating: false,
        error: RESX.HttpError.Error500Title
      }));
      await props.onValueChanged(fieldKey, initialValue);
      return false;
    }
    return true;
  }

  const saveInPageEdit = async (value: any) => {
    if (equal(value, editingField.initialValue)) {
      clearInPageEdit();
      return;
    }

    if (editingField.isUpdating) return;

    const validatorResult = await props.validate();
    if (validatorResult.hasErrors) {
      return;
    }

    setEditingField(state => ({ ...state, isUpdating: true }));
    const success = await executeSubmit(editingField.fieldKey, value, editingField.initialValue, editingField.submitFunc);
    if (success) clearInPageEdit();
  }

  const removeItem = async (field: InPageEditableSectionField) => {
    if (editingField.isUpdating) return;

    if (editingField.fieldKey !== undefined) {
      clearInPageEdit();
    }

    const validatorResult = await props.validate();
    if (validatorResult.hasErrors) {
      return;
    }

    setEditingField({
      fieldKey: field.key,
      value: undefined,
      initialValue: field.inPageEdit!.initialValue,
      isUpdating: true,
      error: undefined,
      submitFunc: field.inPageEdit!.submitChange,
    });

    const success = await executeSubmit(field.key, undefined, field.inPageEdit!.initialValue, field.inPageEdit!.submitChange);
    if (success) clearInPageEdit();
  }

  return <InPageEditingContext.Provider value={{
    enabled: props.enabled,
    editingField,
    startInPageEdit,
    updateValue,
    saveInPageEdit,
    clearInPageEdit,
    removeItem,
  }}>
    {props.children}
  </InPageEditingContext.Provider>
}